import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeading } from "@/components/SectionHeading";
import { PhoneMockup } from "@/components/PhoneMockup";
import { BrowserMockup } from "@/components/BrowserMockup";
import { SCREENS, WEB_APP_URL, WEB_APP_ENABLED } from "@/data/content";

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: [0.2, 0.7, 0.2, 1] },
});

const Case = ({ item, index, labels }) => {
  const reversed = index % 2 === 1;
  const src = item.screen ? SCREENS[item.screen] : null;
  const src2 = item.screen2 ? SCREENS[item.screen2] : null;
  const isBrowser = item.device === "browser";

  return (
    <article
      id={`praxis-${item.id}`}
      data-testid={`praxis-case-${item.id}`}
      className={`grid items-center gap-12 lg:grid-cols-12 lg:gap-10 ${isBrowser ? "" : ""}`}
    >
      <motion.div {...reveal()} className={`${isBrowser ? "lg:col-span-5" : "lg:col-span-6"} ${reversed ? "lg:order-2" : ""}`}>
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-[var(--olive-2)]">{String(index + 1).padStart(2, "0")}</span>
          <span className="h-px w-8 bg-[var(--olive)]/60" />
          <span className="eyebrow">{item.kicker}</span>
        </div>
        <h3 className="font-display mt-5 text-3xl leading-[1.08] text-[var(--ink)] sm:text-4xl">{item.title}</h3>
        <p className="mt-6 text-base leading-relaxed text-[var(--ink-2)] md:text-lg">{item.story}</p>

        <p className="eyebrow mt-9 !text-[0.62rem] text-[var(--ink-3)]">{labels.stepsLabel}</p>
        <ol className="mt-4 space-y-3">
          {item.steps.map((step, i) => (
            <li key={step} className="flex items-start gap-4">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--line-strong)] bg-[var(--surface)] font-mono text-[11px] text-[var(--sage)]">
                {i + 1}
              </span>
              <span className="text-sm leading-relaxed text-[var(--ink)] md:text-base">{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-8 rounded-2xl border border-[var(--olive)]/40 bg-[var(--olive)]/10 p-5">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--sage)]" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--sage)]">{labels.benefitLabel}</span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-[var(--ink)] md:text-base">{item.benefit}</p>
        </div>

        {item.webCta && WEB_APP_ENABLED && (
          <a
            href={WEB_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost mt-6 px-5 py-2.5 text-sm"
            data-testid={`praxis-web-cta-${item.id}`}
          >
            {item.webCta}
            <ArrowUpRight className="h-4 w-4" />
          </a>
        )}
      </motion.div>

      <motion.div {...reveal(0.15)} className={`${isBrowser ? "lg:col-span-7" : "lg:col-span-6"} ${reversed ? "lg:order-1" : ""}`}>
        <div className="relative flex justify-center">
          <div className="absolute inset-x-10 top-1/4 bottom-1/4 rounded-full bg-[var(--olive)]/15 blur-3xl" aria-hidden="true" />
          {isBrowser ? (
            <BrowserMockup src={src} alt={item.screenLabel} label={item.screenLabel} className="relative w-full max-w-[720px]" testId={`praxis-browser-${item.id}`} />
          ) : src2 ? (
            <div className="relative h-[600px] w-full max-w-[460px] sm:h-[680px]">
              <div className="absolute right-0 top-0 w-[250px] sm:w-[280px]">
                <PhoneMockup src={src} alt={item.screenLabel} label={item.screenLabel} testId={`praxis-phone-${item.id}`} />
              </div>
              <div className="absolute bottom-0 left-0 w-[180px] sm:w-[210px]">
                <PhoneMockup src={src2} alt={item.screenLabel2} label={item.screenLabel2} testId={`praxis-phone2-${item.id}`} />
              </div>
            </div>
          ) : (
            <PhoneMockup src={src} alt={item.screenLabel} label={item.screenLabel} className="relative w-[260px] sm:w-[290px]" testId={`praxis-phone-${item.id}`} />
          )}
        </div>
      </motion.div>
    </article>
  );
};

export const Praxis = () => {
  const { t } = useLanguage();
  const s = t.praxis;
  return (
    <section id="praxis" className="relative scroll-mt-24 py-24 md:py-32">
      <div className="hairline absolute inset-x-0 top-0 mx-auto max-w-5xl" />
      <div className="mx-auto max-w-7xl px-5 sm:px-6 md:px-8">
        <SectionHeading index={s.index} eyebrow={s.eyebrow} title={s.title} body={s.body} />
        <div className="mt-20 space-y-28 lg:space-y-36">
          {s.cases.map((item, i) => (
            <Case key={item.id} item={item} index={i} labels={s} />
          ))}
        </div>
      </div>
    </section>
  );
};
